import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { X, ArrowUp, Minus, MessageCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { startSupportChat, sendSupportMessage, closeSupportChat } from '../lib/api';
import useSupportChatStore from '../stores/useSupportChatStore';
import ConfirmationModal from './ConfirmationModal';
import './SupportChat.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function getSFSubtitle() {
  const now = new Date();
  const sfTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  const hour = sfTime.getHours();
  if (hour >= 9 && hour < 21) {
    return 'We typically reply within under a minute';
  }
  const formatted = sfTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Los_Angeles',
  });
  return `It's currently ${formatted} for our team. We will make an effort to respond ASAP, if not we will reply through email`;
}
const SIDEBAR_FULL_WIDTH = 220;
const SIDEBAR_COLLAPSED_WIDTH = 56;
const DEFAULT_WIDTH = 320;
const DEFAULT_HEIGHT = 480;
const MIN_WIDTH = 260;
const MIN_HEIGHT = 200;
const PANEL_GAP = 8;
const FLOATING_PANEL_MARGIN = 24;
// Approximate header-only height (title + padding, no subtitle) for bottom-anchor math
const MINIMIZED_HEIGHT = 42;

function getInitialFrame(placement, sidebarCollapsed, bottomOffset) {
  const viewportWidth = window.innerWidth || DEFAULT_WIDTH + (FLOATING_PANEL_MARGIN * 2);
  const viewportHeight = window.innerHeight || DEFAULT_HEIGHT + (FLOATING_PANEL_MARGIN * 2);
  const maxFloatingWidth = Math.max(240, viewportWidth - (FLOATING_PANEL_MARGIN * 2));
  const maxFloatingHeight = Math.max(MIN_HEIGHT, viewportHeight - bottomOffset);
  const width = placement === 'floating'
    ? Math.min(DEFAULT_WIDTH, maxFloatingWidth)
    : DEFAULT_WIDTH;
  const height = Math.min(DEFAULT_HEIGHT, maxFloatingHeight);
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_FULL_WIDTH;

  return {
    size: { width, height },
    position: {
      x: placement === 'floating'
        ? Math.max(PANEL_GAP, viewportWidth - width - FLOATING_PANEL_MARGIN)
        : sidebarWidth + PANEL_GAP,
      y: Math.max(MINIMIZED_HEIGHT + PANEL_GAP, viewportHeight - bottomOffset),
    },
  };
}

export default function SupportChat({
  onClose,
  sidebarCollapsed,
  placement = 'sidebar',
  bottomOffset = FLOATING_PANEL_MARGIN,
}) {
  const { sessionId, messages, isLoading, isSending } = useSupportChatStore();
  const setSession = useSupportChatStore((s) => s.setSession);
  const appendMessage = useSupportChatStore((s) => s.appendMessage);
  const setLoading = useSupportChatStore((s) => s.setLoading);
  const setSending = useSupportChatStore((s) => s.setSending);
  const resetChat = useSupportChatStore((s) => s.resetChat);

  const [input, setInput] = useState('');
  const [subtitle] = useState(() => getSFSubtitle());
  const [isMinimized, setIsMinimized] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const initialFrameRef = useRef(null);
  if (!initialFrameRef.current) {
    initialFrameRef.current = getInitialFrame(placement, sidebarCollapsed, bottomOffset);
  }

  // position.y is the BOTTOM anchor — kept fixed when collapsing/expanding
  const [position, setPosition] = useState(initialFrameRef.current.position);
  const [size, setSize] = useState(initialFrameRef.current.size);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const dragStateRef = useRef(null);

  useEffect(() => {
    if (!isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isMinimized]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const session = await startSupportChat();
        if (cancelled) return;
        setSession(session);
        const { data: { session: supaSession } } = await supabase.auth.getSession();
        const token = supaSession?.access_token;
        if (!token || cancelled) return;
        const socket = io(API_BASE_URL, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
        });
        socketRef.current = socket;
        socket.on('connect', () => socket.emit('join_support'));
        socket.on('support_message', (msg) => appendMessage(msg));
      } catch (err) {
        console.error('[SupportChat] Failed to start session:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsDragging(true);
    document.body.style.userSelect = 'none';
    dragStateRef.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPanelX: position.x,
      startPanelY: position.y,
    };

    const onMouseMove = (e) => {
      const { startMouseX, startMouseY, startPanelX, startPanelY } = dragStateRef.current;
      setPosition({
        x: Math.max(0, startPanelX + (e.clientX - startMouseX)),
        y: Math.max(MINIMIZED_HEIGHT, startPanelY + (e.clientY - startMouseY)),
      });
    };

    const onMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    document.body.style.userSelect = 'none';

    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.width;
    const startH = size.height;
    const startPX = position.x;
    const startBottomY = position.y;

    const onMouseMove = (e) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      let newW = startW;
      let newH = startH;
      let newPX = startPX;

      if (direction === 'e' || direction === 'se') {
        newW = Math.max(MIN_WIDTH, startW + dx);
      }
      if (direction === 'w') {
        newW = Math.max(MIN_WIDTH, startW - dx);
        newPX = startPX + (startW - newW);
      }
      if (direction === 's' || direction === 'se') {
        // Dragging the bottom edge moves the bottom anchor
        const clampedDy = Math.max(MIN_HEIGHT - startH, dy);
        newH = startH + clampedDy;
        setPosition((prev) => ({ ...prev, x: direction === 'w' ? newPX : prev.x, y: startBottomY + clampedDy }));
      }
      setSize({ width: newW, height: newH });
      if (direction === 'w') setPosition((prev) => ({ ...prev, x: newPX }));
    };

    const onMouseUp = () => {
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !sessionId || isSending) return;
    setInput('');
    setSending(true);
    try {
      const msg = await sendSupportMessage(sessionId, text);
      appendMessage(msg);
    } catch (err) {
      console.error('[SupportChat] Failed to send message:', err);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCloseRequest = () => {
    if (sessionId && messages.length > 0) {
      setShowCloseConfirm(true);
    } else {
      handleConfirmClose();
    }
  };

  const handleConfirmClose = async () => {
    setShowCloseConfirm(false);
    socketRef.current?.disconnect();
    socketRef.current = null;
    if (sessionId) {
      try {
        await closeSupportChat(sessionId);
      } catch (err) {
        console.error('[SupportChat] Failed to close session:', err);
      }
    }
    resetChat();
    onClose();
  };

  return (
    <>
      <div
        className={`support-chat support-chat--${placement}${isMinimized ? ' support-chat--minimized' : ''}${isDragging ? ' support-chat--dragging' : ''}`}
        style={{
          left: position.x,
          top: isMinimized ? position.y - MINIMIZED_HEIGHT : position.y - size.height,
          width: size.width,
          ...(isMinimized ? {} : { height: size.height }),
        }}
      >
        <div className="support-chat__header" onMouseDown={handleDragStart}>
          <div className="support-chat__header-info">
            <span className="support-chat__status-dot" />
            <div className="support-chat__header-text">
              <span className="support-chat__header-title">Support Chat</span>
              {!isMinimized && (
                <span className="support-chat__header-subtitle">{subtitle}</span>
              )}
            </div>
          </div>
          <div className="support-chat__header-actions">
            <button
              type="button"
              className="support-chat__icon-btn"
              onClick={(e) => { e.stopPropagation(); setIsMinimized((p) => !p); }}
              aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
            >
              <Minus size={14} />
            </button>
            <button
              type="button"
              className="support-chat__icon-btn support-chat__icon-btn--close"
              onClick={(e) => { e.stopPropagation(); handleCloseRequest(); }}
              aria-label="Close support chat"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            <div className="support-chat__messages">
              {isLoading && messages.length === 0 && (
                <div className="support-chat__loading">
                  <span className="support-chat__loading-dots">
                    <span /><span /><span />
                  </span>
                  Connecting…
                </div>
              )}
              {!isLoading && messages.length === 0 && (
                <div className="support-chat__empty">
                  <MessageCircle size={22} />
                  <p>Send us a message and we'll get back to you as soon as possible.</p>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`support-chat__message support-chat__message--${msg.sender}`}
                >
                  <div className="support-chat__bubble">{msg.content}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            <div className="support-chat__input-area">
              <textarea
                ref={textareaRef}
                className="support-chat__textarea"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message…"
                rows={1}
                disabled={isLoading || !sessionId}
              />
              <button
                type="button"
                className="support-chat__send-btn"
                onClick={handleSend}
                disabled={!input.trim() || isSending || isLoading || !sessionId}
                aria-label="Send message"
              >
                <ArrowUp size={14} />
              </button>
            </div>

            <div
              className="support-chat__resize-handle support-chat__resize-handle--e"
              onMouseDown={(e) => handleResizeStart(e, 'e')}
            />
            <div
              className="support-chat__resize-handle support-chat__resize-handle--s"
              onMouseDown={(e) => handleResizeStart(e, 's')}
            />
            <div
              className="support-chat__resize-handle support-chat__resize-handle--se"
              onMouseDown={(e) => handleResizeStart(e, 'se')}
            />
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={showCloseConfirm}
        title="End support session?"
        message="Closing this chat will end the current support session. Your message history may no longer be accessible."
        confirmLabel="End session"
        cancelLabel="Keep open"
        destructive
        onConfirm={handleConfirmClose}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
}
